import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Wine, Filter, X, Plus, ArrowLeft, SlidersHorizontal, Home, ShoppingCart, User } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';
import AgeGate from '@/components/AgeGate';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nj-liquor-delivery/artifacts/q14l1b4u_1001594216.jpg";

const CATEGORIES = ["All", "Liquor & Spirits", "Beer & Wine", "Grocery", "Deli & Hot Food"];

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState('popular');
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState(["All"]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const { addItem, itemCount } = useCart();
  const { user, isAdult, verifyAge } = useAuth();
  const navigate = useNavigate();
  const activeCategory = searchParams.get('category') || 'All';
  const [showAgeGate, setShowAgeGate] = useState(false);

  useEffect(() => {
    const ageStatus = localStorage.getItem('age_status');
    if (!ageStatus) {
      setShowAgeGate(true);
    }
  }, []);

  const handleAgeResult = (status) => {
    setShowAgeGate(false);
    verifyAge(status);
  };

  useEffect(() => {
    try {
      const items = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
      const filtered = isAdult ? items : items.filter(p => !p.age_restricted);
      setRecentlyViewed(filtered.slice(0, 5));
    } catch {}
  }, [isAdult]);

  useEffect(() => {
    document.title = "Shop Catalog — Hoboken Liquor Grocery & Deli";
    api.get('/categories').then(res => {
      const names = res.data.categories.map(c => c.name || c);
      const filtered = isAdult ? names : names.filter(n => !['Liquor & Spirits', 'Beer & Wine', 'Wine', 'Beer & FMB', 'Liquor'].includes(n));
      setCategories(["All", ...filtered]);
    }).catch(() => {});
  }, [isAdult, user]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 50 };
      if (activeCategory !== 'All') params.category = activeCategory;
      if (search) params.search = search;
      const res = await api.get('/products', { params });
      let filteredProducts = res.data.products;
      // Filter out alcohol for minors
      if (!isAdult) {
        filteredProducts = filteredProducts.filter(p => !p.age_restricted);
      }
      setProducts(filteredProducts);
      setTotal(isAdult ? res.data.total : filteredProducts.length);
    } catch (e) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [sort, activeCategory, search, isAdult]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleCategoryChange = (cat) => {
    if (cat === 'All') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', cat);
    }
    setSearchParams(searchParams);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock <= 0) return;
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] pb-16" data-testid="shop-page">
      {/* Announcement Bar */}
      <div className="bg-[#cc3737] text-white text-xs font-semibold py-1.5 px-4 text-center fixed top-0 w-full z-50 flex items-center justify-center gap-2" data-testid="announcement-bar">
        <span>🚚 Cash on Delivery Only • Serving Hoboken, NJ • ID Checked at Delivery</span>
      </div>

      {/* Header */}
      <nav className="fixed top-8 w-full z-50 bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4 text-[#94A3B8]" />
              <img src={LOGO_URL} alt="Hoboken LGD" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-['Playfair_Display'] text-lg font-medium text-white hidden sm:block">HOBOKEN LIQUOR GROCERY & DELI</span>
            </Link>
          </div>
          <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-[#12151C] border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38] transition-colors"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/account" className="text-[#94A3B8] hover:text-white text-sm hidden sm:block transition-colors" data-testid="account-link">Hi, {user.name?.split(' ')[0]}</Link>
            ) : (
              <Link to="/login" className="text-[#94A3B8] hover:text-white text-sm transition-colors">Sign In</Link>
            )}
            <Link to="/checkout" className="relative" data-testid="shop-cart-icon">
              <ShoppingBag className="w-5 h-5 text-[#94A3B8] hover:text-white transition-colors" />
              {itemCount > 0 && <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#cc3737] text-[10px] text-white flex items-center justify-center">{itemCount}</span>}
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-28 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-12">
        {/* Category Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide" data-testid="category-tabs">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCategory === cat ? 'bg-[#cc3737] text-white' : 'bg-[#12151C] text-[#94A3B8] border border-white/5 hover:border-white/15'}`}
              data-testid={`category-tab-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Sort & Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-[#64748B] text-sm">{total} products</p>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-[#12151C] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#b56b38]"
            data-testid="sort-select"
          >
            <option value="popular">Popular</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="newest">Newest</option>
          </select>
        </div>

        {/* Product Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-[#12151C] rounded-xl aspect-[3/4] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#64748B] text-lg">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="product-grid">
            {products.map((product, i) => (
              <Link
                to={`/shop/product/${product.id}`}
                key={product.id}
                className="product-card bg-[#12151C] border border-white/5 rounded-xl overflow-hidden group"
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`product-card-${i}`}
              >
                <div className="relative aspect-square overflow-hidden bg-[#1A1D27]">
                  <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                  <div className="absolute top-2 left-2 flex gap-1">
                    {product.age_restricted && <span className="age-badge">21+</span>}
                    {product.sale_price && <span className="sale-badge">SALE</span>}
                  </div>
                  {product.stock <= 0 && (
                    <div className="absolute inset-0 bg-[#0B0E14]/70 flex items-center justify-center">
                      <span className="text-white font-medium text-sm">Out of Stock</span>
                    </div>
                  )}
                  <button
                    onClick={(e) => handleAddToCart(e, product)}
                    className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-[#cc3737] hover:bg-[#d94444] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0"
                    data-testid={`add-to-cart-btn-${i}`}
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="p-3">
                  <p className="text-[#64748B] text-xs font-light mb-1 truncate">{product.brand}</p>
                  <h3 className="text-white text-sm font-medium mb-2 line-clamp-2 leading-tight">{product.name}</h3>
                  <div className="flex items-center gap-2">
                    {product.sale_price ? (
                      <>
                        <span className="text-[#b56b38] font-semibold text-sm">${product.sale_price.toFixed(2)}</span>
                        <span className="text-[#64748B] text-xs line-through">${product.price.toFixed(2)}</span>
                      </>
                    ) : (
                      <span className="text-white font-semibold text-sm">${product.price.toFixed(2)}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Recently Viewed Products */}
        {recentlyViewed.length > 0 && (
          <div className="mt-16 border-t border-white/5 pt-10" data-testid="recently-viewed-section">
            <h3 className="font-['Playfair_Display'] text-xl text-white mb-6">Recently Viewed Products</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {recentlyViewed.map((product, i) => (
                <Link
                  to={`/shop/product/${product.id}`}
                  key={product.id}
                  className="product-card bg-[#12151C] border border-white/5 rounded-xl overflow-hidden group"
                  data-testid={`recently-viewed-card-${i}`}
                >
                  <div className="relative aspect-square overflow-hidden bg-[#1A1D27]">
                    <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute top-2 left-2 flex gap-1">
                      {product.age_restricted && <span className="age-badge">21+</span>}
                      {product.sale_price && <span className="sale-badge">SALE</span>}
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-[#64748B] text-xs font-light mb-1 truncate">{product.brand}</p>
                    <h3 className="text-white text-sm font-medium mb-1 line-clamp-2 leading-tight">{product.name}</h3>
                    <span className="text-[#b56b38] font-semibold text-sm">${(product.sale_price || product.price).toFixed(2)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Nav */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0B0E14] border-t border-white/10 sm:hidden" data-testid="shop-mobile-nav">
        <div className="flex justify-around py-2">
          <Link to="/" className="flex flex-col items-center gap-0.5 text-[#64748B]"><Home className="w-5 h-5" /><span className="text-[9px]">Home</span></Link>
          <Link to="/shop" className="flex flex-col items-center gap-0.5 text-[#cc3737]"><ShoppingBag className="w-5 h-5" /><span className="text-[9px]">Shop</span></Link>
          <Link to="/checkout" className="relative flex flex-col items-center gap-0.5 text-[#64748B]"><ShoppingCart className="w-5 h-5" />{itemCount > 0 && <span className="absolute -top-1 right-0 w-4 h-4 rounded-full bg-[#cc3737] text-[9px] text-white flex items-center justify-center">{itemCount}</span>}<span className="text-[9px]">Cart</span></Link>
          <Link to="/account" className="flex flex-col items-center gap-0.5 text-[#64748B]"><User className="w-5 h-5" /><span className="text-[9px]">Account</span></Link>
        </div>
      </div>

      {showAgeGate && <AgeGate onResult={handleAgeResult} />}
    </div>
  );
}
